// The capability gate (P0.9) — FR-42's posture, applied to the one capability the engine cannot
// start without.
//
// "Unsupported browsers and mobile devices get a friendly explanation page, never a broken app."
// The full gate — mobile detection, Tier 1/2 branching, AudioWorklet and WebGL2 probes — belongs
// to the app shell. This is the isolation half, and it exists now because P0.9 is the prompt where
// a real deployment can get it wrong.
//
// Each message names the layer that failed, because the fix is somewhere different every time:
// the user's URL, the user's browser, the page that framed us, or our own hosting. A gate that
// says "something went wrong" has told the user nothing and told us less.

import type { JSX } from 'react';

import type { IsolationFailure, IsolationStatus } from '../platform/isolation';

interface Explanation {
  readonly heading: string;
  readonly detail: string;
  /** What the person reading this can actually do. Null when it is ours to fix, not theirs. */
  readonly action: string | null;
}

const EXPLANATIONS: Readonly<Record<IsolationFailure, Explanation>> = {
  'insecure-context': {
    heading: 'SYNAPSE needs a secure connection',
    detail:
      'Browsers only allow the high-performance audio features SYNAPSE is built on over HTTPS. ' +
      'This page was loaded over an insecure connection.',
    action: 'Open this page over https:// and it will work.',
  },
  'unsupported-browser': {
    heading: 'This browser is too old for SYNAPSE',
    detail:
      'SYNAPSE runs its audio engine on a dedicated real-time thread, which needs browser features ' +
      'added around 2020. This browser does not have them.',
    action: 'A current version of Chrome, Edge, Firefox or Safari will work.',
  },
  'embedded-cross-origin': {
    heading: 'SYNAPSE cannot run inside another site',
    detail:
      'This page has been embedded in a frame on another site. The isolation SYNAPSE needs for ' +
      'glitch-free audio is not available to embedded pages — this is a deliberate browser rule, ' +
      'not a bug.',
    action: 'Open SYNAPSE in its own tab.',
  },
  'headers-missing': {
    heading: 'SYNAPSE is not configured correctly here',
    detail:
      'The server did not send the cross-origin isolation headers the audio engine needs. This is ' +
      'a problem with how this copy of SYNAPSE is hosted, not with your browser or your machine.',
    // Deliberately null: this failure is ours. Telling the user to try another browser would be
    // sending them to chase a fault they cannot reach.
    action: null,
  },
};

export function CapabilityGate({ status }: { status: IsolationStatus }): JSX.Element {
  const explanation = status.failure === null ? null : EXPLANATIONS[status.failure];

  return (
    <main>
      <h1>SYNAPSE</h1>

      <h2>{explanation?.heading ?? 'SYNAPSE cannot start'}</h2>
      <p>
        {explanation?.detail ?? 'The audio engine could not be started on this page.'}
      </p>
      {explanation?.action !== null && explanation !== null && (
        <p>{explanation.action}</p>
      )}

      {/* The diagnostics, plainly. Not decoration: "headers-missing" reaching a real user is a
          deployment fault, and this is what they can paste into a bug report. */}
      <details>
        <summary>Technical details</summary>
        <dl>
          <dt>crossOriginIsolated</dt>
          <dd>{String(status.crossOriginIsolated)}</dd>

          <dt>SharedArrayBuffer</dt>
          <dd>{status.sharedArrayBuffer ? 'available' : 'unavailable'}</dd>

          <dt>Secure context</dt>
          <dd>{String(status.secureContext)}</dd>

          <dt>Diagnosis</dt>
          <dd>{status.failure ?? 'none'}</dd>
        </dl>
      </details>
    </main>
  );
}
